// ============================================================================
// ClientRoadmapPage (Iter43 — post-deploy)
// ---------------------------------------------------------------------------
// Founder-only view. Groups all projects BY CLIENT and shows, for each client,
// a "prestation roadmap" : where we are in the deliverable pipeline.
//
// Layout:
//   Left  : list of clients (sorted by activity), each row shows client name +
//           active projects count + next deadline. Click to select.
//   Right : selected client detail — overall progress, project timeline
//           (in-progress / done / upcoming), quick "New project" CTA.
//
// Interactive : click on a project → opens it in ProjectsPage (via localStorage
// bridge `asa_open_project`). Click on the "+ Nouveau projet" CTA to create a
// project directly for this client (with client name pre-filled).
// ============================================================================
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { PageHeader } from '../components/AppLayout';
import { useIsMobile } from '../hooks/useIsMobile';
import MobileRoadmapPage from './MobileRoadmapPage';
import {
  Briefcase, Calendar, Plus, LayoutGrid, Check, Clock, AlertTriangle, ArrowRight,
  ProjectIcon, ChevronRight, Trash2, Folder, MoreVertical, PanelLeftClose, PanelLeftOpen,
  PanelRightClose, PanelRightOpen, Pencil,
} from '../components/icons';

const STAGE_LABEL = {
  todo:        { label: 'À faire',   color: '#94A3B8' },
  in_progress: { label: 'En cours',  color: '#5BBEFC' },
  review:      { label: 'À valider', color: '#FBBF24' },
  done:        { label: 'Terminé',   color: '#34D399' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch { return iso; }
};

export default function ClientRoadmapPage() {
  const isMobile = useIsMobile();
  if (isMobile) return <MobileRoadmapPage />;
  return <ClientRoadmapPageDesktop />;
}

function ClientRoadmapPageDesktop() {
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [deliveryMap, setDeliveryMap] = useState({});
  const [collapsedFolders, setCollapsedFolders] = useState({});
  // Iter47 — collapsible side panels (persist state)
  const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem('asa_roadmap_sidebar') !== 'closed');
  const [projectsOpen, setProjectsOpen] = useState(() => localStorage.getItem('asa_roadmap_projects') !== 'closed');
  const toggleSidebar = () => setSidebarOpen((v) => { const n = !v; localStorage.setItem('asa_roadmap_sidebar', n ? 'open' : 'closed'); return n; });
  const toggleProjects = () => setProjectsOpen((v) => { const n = !v; localStorage.setItem('asa_roadmap_projects', n ? 'open' : 'closed'); return n; });

  const reload = async () => {
    try {
      const [rp, rd] = await Promise.all([
        api.get('/projects'),
        api.get('/client-delivery').catch(() => ({ data: [] })),
      ]);
      setProjects(rp.data || []);
      const map = {};
      for (const d of (rd.data || [])) map[d.client_key] = d;
      setDeliveryMap(map);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  // Group projects by client
  const clients = useMemo(() => {
    const map = new Map();
    for (const p of projects) {
      const c = (p.client || '—').trim();
      if (!map.has(c)) map.set(c, []);
      map.get(c).push(p);
    }
    // Iter43d — Include standalone roadmaps (delivery docs with NO matching project)
    // so a user can create/see a roadmap without being forced to create a project.
    for (const [key, doc] of Object.entries(deliveryMap)) {
      const displayName = doc.client_name || key;
      if (!map.has(displayName) && !Array.from(map.keys()).some((k) => k.toLowerCase() === key)) {
        map.set(displayName, []);
      }
    }
    // Turn into array with stats
    const arr = Array.from(map.entries()).map(([client, ps]) => {
      const active = ps.filter((p) => p.progress?.ratio < 1);
      const done = ps.filter((p) => p.progress?.ratio >= 1);
      const nextDeadline = ps
        .map((p) => p.end_date)
        .filter(Boolean)
        .sort()[0];
      const avgRatio = ps.length ? (ps.reduce((a, p) => a + (p.progress?.ratio || 0), 0) / ps.length) : 0;
      const totalTasks = ps.reduce((a, p) => a + (p.progress?.total || 0), 0);
      const doneTasks = ps.reduce((a, p) => a + (p.progress?.done || 0), 0);
      // Iter43b — inject display_name + folder from client_delivery doc
      const dkey = client.toLowerCase();
      const doc = deliveryMap[dkey] || {};
      const display = doc.display_name || client;
      const folder = doc.folder || 'Sans dossier';
      const standalone = ps.length === 0;
      return { client, display, folder, projects: ps, active, done, nextDeadline, avgRatio, totalTasks, doneTasks, standalone };
    });
    // Sort : most active first, then next-deadline soonest
    arr.sort((a, b) => {
      if (a.active.length !== b.active.length) return b.active.length - a.active.length;
      return (a.nextDeadline || '9999').localeCompare(b.nextDeadline || '9999');
    });
    return arr;
  }, [projects, deliveryMap]);

  // Iter43b — group clients by folder for the sidebar
  const clientsByFolder = useMemo(() => {
    const grouped = {};
    for (const c of clients) {
      const f = c.folder || 'Sans dossier';
      if (!grouped[f]) grouped[f] = [];
      grouped[f].push(c);
    }
    // Sort folders alphabetically, but keep "Sans dossier" last
    const keys = Object.keys(grouped).sort((a, b) => {
      if (a === 'Sans dossier') return 1;
      if (b === 'Sans dossier') return -1;
      return a.localeCompare(b);
    });
    return keys.map((k) => ({ folder: k, clients: grouped[k] }));
  }, [clients]);

  // Auto-select first client on load
  useEffect(() => {
    if (!selectedClient && clients.length > 0) setSelectedClient(clients[0].client);
  }, [clients, selectedClient]);

  const selected = clients.find((c) => c.client === selectedClient);

  const openProject = (p) => {
    localStorage.setItem('asa_open_project', p.id);
    nav('/projects');
  };

  const createProjectForClient = (clientName) => {
    // Bridge to /projects page — the wizard picker opens when a query param is set.
    // Since ProjectsPage doesn't natively support pre-filled client, we just navigate
    // and let the founder start the wizard manually. TODO: wire deep-link with client=.
    localStorage.setItem('asa_new_project_client', clientName);
    nav('/projects');
    toast.info(`Ouvre "Nouveau" — client "${clientName}" à pré-remplir dans le wizard`);
  };

  return (
    <div data-testid="client-roadmap-page">
      <PageHeader
        title="Roadmap client"
        subtitle="Vue founder — Où en est-on de la prestation par client ?"
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontFamily: 'Sora', fontSize: 11, color: 'var(--t3)' }}>
              {clients.length} client{clients.length > 1 ? 's' : ''} · {projects.length} projet{projects.length > 1 ? 's' : ''}
            </span>
          </div>
        }
      />
      <div style={{ padding: '0 22px 100px' }}>
        {loading ? (
          <div className="shimmer" style={{ height: 400, borderRadius: 14 }} />
        ) : clients.length === 0 ? (
          <div className="asa-card" style={{ padding: 32, textAlign: 'center', color: 'var(--t3)' }}>
            <Briefcase size={32} />
            <div style={{ marginTop: 12, fontFamily: 'Lexend' }}>Aucun projet · aucun client à afficher pour le moment.</div>
          </div>
        ) : (
          <div className="roadmap-3col-layout" style={{
            display: 'grid',
            gridTemplateColumns: `${sidebarOpen ? '260px' : '44px'} 1fr ${projectsOpen ? '280px' : '44px'}`,
            gap: 14, alignItems: 'start',
            transition: 'grid-template-columns 0.25s var(--ease-out)',
          }}>
            {/* LEFT : client list — collapsible */}
            {sidebarOpen ? (
              <div data-testid="client-list" className="asa-card" style={{
                padding: 8, display: 'flex', flexDirection: 'column', gap: 3,
                maxHeight: 'calc(100vh - 180px)', overflowY: 'auto',
                position: 'sticky', top: 18,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 6px 2px' }}>
                  <span style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 11, color: 'var(--t2)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Roadmaps
                  </span>
                  <button
                    data-testid="roadmap-sidebar-collapse"
                    onClick={toggleSidebar}
                    title="Réduire"
                    style={{
                      padding: 4, borderRadius: 6, background: 'transparent',
                      border: 'none', color: 'var(--t3)', cursor: 'pointer',
                    }}
                  ><PanelLeftClose size={13} /></button>
                </div>
                <button
                  data-testid="new-roadmap-btn"
                  onClick={async () => {
                    const name = window.prompt('Nom du client / de la roadmap ?');
                    if (!name || !name.trim()) return;
                    try {
                      await api.get(`/client-delivery/${encodeURIComponent(name.trim())}`);
                      toast.success('Roadmap créée');
                      await reload();
                      setSelectedClient(name.trim());
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Erreur');
                    }
                  }}
                  className="asa-btn"
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    fontSize: 11, padding: '7px 10px', margin: '4px 4px 8px',
                  }}
                >
                  <Plus size={12} /> Nouvelle roadmap
                </button>
                {clientsByFolder.map((grp) => {
                  const isCollapsed = !!collapsedFolders[grp.folder];
                  return (
                    <div key={grp.folder} style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 6 }}>
                      <button
                        data-testid={`folder-header-${grp.folder.replace(/[^A-Za-z0-9]/g, '_')}`}
                        onClick={() => setCollapsedFolders((prev) => ({ ...prev, [grp.folder]: !prev[grp.folder] }))}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          padding: '6px 10px', borderRadius: 8,
                          background: 'transparent', color: 'var(--t3)',
                          fontFamily: 'Sora', fontWeight: 700, fontSize: 10,
                          letterSpacing: '0.08em', textTransform: 'uppercase',
                          cursor: 'pointer', textAlign: 'left', border: 'none',
                        }}
                      >
                        <ChevronRight size={10} style={{ transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)', transition: 'transform 0.15s' }} />
                        <Folder size={11} color="var(--t3)" strokeWidth={1.8} />
                        <span style={{ flex: 1 }}>{grp.folder}</span>
                        <span style={{ color: 'var(--t3)', fontSize: 10 }}>{grp.clients.length}</span>
                      </button>
                      {!isCollapsed && grp.clients.map((c) => (
                  <button
                    key={c.client}
                    data-testid={`client-row-${c.client.replace(/[^A-Za-z0-9]/g, '_')}`}
                    onClick={() => setSelectedClient(c.client)}
                    style={{
                      display: 'flex', flexDirection: 'column', gap: 4,
                      padding: '10px 12px', borderRadius: 10,
                      background: selectedClient === c.client ? 'rgba(91,190,252,0.10)' : 'transparent',
                      border: '1.5px solid ' + (selectedClient === c.client ? '#5BBEFC55' : 'transparent'),
                      cursor: 'pointer', textAlign: 'left',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        width: 22, height: 22, borderRadius: 6,
                        background: 'rgba(91,190,252,0.15)',
                        display: 'grid', placeItems: 'center',
                      }}>
                        <Briefcase size={10} color="#5BBEFC" />
                      </span>
                      <span style={{
                        fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
                        color: 'var(--t1)', flex: 1,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {c.display}
                      </span>
                      <span style={{
                        fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5,
                        padding: '2px 6px', borderRadius: 999,
                        background: c.active.length > 0 ? 'rgba(91,190,252,0.16)' : 'rgba(52,211,153,0.16)',
                        color: c.active.length > 0 ? '#5BBEFC' : '#34D399',
                      }}>
                        {c.active.length > 0 ? `${c.active.length}` : '✓'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, fontFamily: 'Lexend', fontSize: 10, color: 'var(--t3)' }}>
                      <span>{c.projects.length} projet{c.projects.length > 1 ? 's' : ''}</span>
                      {c.nextDeadline && <>
                        <span>·</span>
                        <span>{fmtDate(c.nextDeadline)}</span>
                      </>}
                    </div>
                    <div style={{ height: 3, borderRadius: 2, background: 'var(--sf2)', overflow: 'hidden', marginTop: 2 }}>
                      <div style={{
                        width: `${Math.round(c.avgRatio * 100)}%`,
                        height: '100%',
                        background: c.avgRatio >= 1 ? '#34D399' : '#5BBEFC',
                        transition: 'width 0.4s var(--ease-out)',
                      }} />
                    </div>
                  </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            ) : (
              <button
                data-testid="roadmap-sidebar-expand"
                onClick={toggleSidebar}
                title="Ouvrir la liste des roadmaps"
                style={{
                  width: 36, padding: '10px 6px', borderRadius: 10,
                  background: 'var(--sf)', border: '1px solid var(--bd)',
                  color: 'var(--t2)', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                  position: 'sticky', top: 18,
                }}
              >
                <PanelLeftOpen size={14} />
                <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontFamily: 'Sora', fontWeight: 700, fontSize: 10, letterSpacing: '0.08em' }}>ROADMAPS</span>
              </button>
            )}

            {/* MIDDLE : selected client detail */}
            {selected ? (
              <ClientDetail
                data={selected}
                onOpenProject={openProject}
                onCreate={() => createProjectForClient(selected.client)}
                onDeliveryChange={(doc) => {
                  if (doc.deleted) {
                    setDeliveryMap((prev) => {
                      const next = { ...prev };
                      delete next[doc.client_key];
                      return next;
                    });
                    setSelectedClient(null);
                  } else {
                    setDeliveryMap((prev) => ({ ...prev, [doc.client_key]: doc }));
                  }
                }}
                hideActiveInDetail
              />
            ) : (
              <div className="asa-card" style={{ padding: 32, textAlign: 'center', color: 'var(--t3)' }}>
                Sélectionne un client à gauche pour voir sa roadmap.
              </div>
            )}

            {/* RIGHT : Projets en cours — collapsible */}
            {projectsOpen ? (
              <div className="asa-card" style={{
                padding: 14, position: 'sticky', top: 18,
                maxHeight: 'calc(100vh - 180px)', overflowY: 'auto',
              }} data-testid="active-projects-panel">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Clock size={12} color="#5BBEFC" strokeWidth={2} />
                    <span style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 11, color: 'var(--t2)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      Projets en cours
                    </span>
                    {selected?.active?.length > 0 && (
                      <span style={{
                        padding: '1px 7px', borderRadius: 999,
                        background: 'rgba(91,190,252,0.18)', color: '#5BBEFC',
                        fontFamily: 'Sora', fontWeight: 800, fontSize: 10,
                      }}>{selected.active.length}</span>
                    )}
                  </div>
                  <button
                    data-testid="roadmap-projects-collapse"
                    onClick={toggleProjects}
                    title="Réduire"
                    style={{ padding: 4, borderRadius: 6, background: 'transparent', border: 'none', color: 'var(--t3)', cursor: 'pointer' }}
                  ><PanelRightClose size={13} /></button>
                </div>
                {selected && selected.active.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {selected.active.map((p) => (
                      <ProjectCompactRow key={p.id} project={p} onOpen={() => openProject(p)} />
                    ))}
                  </div>
                ) : (
                  <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)', textAlign: 'center', padding: '20px 6px', opacity: 0.7 }}>
                    Aucun projet en cours pour ce client.
                  </div>
                )}
              </div>
            ) : (
              <button
                data-testid="roadmap-projects-expand"
                onClick={toggleProjects}
                title="Ouvrir les projets en cours"
                style={{
                  width: 36, padding: '10px 6px', borderRadius: 10,
                  background: 'var(--sf)', border: '1px solid var(--bd)',
                  color: 'var(--t2)', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                  position: 'sticky', top: 18,
                }}
              >
                <PanelRightOpen size={14} />
                <span style={{ writingMode: 'vertical-rl', fontFamily: 'Sora', fontWeight: 700, fontSize: 10, letterSpacing: '0.08em' }}>PROJETS</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Compact project row for the right panel
function ProjectCompactRow({ project, onOpen }) {
  const ratio = project.progress?.ratio || 0;
  const pct = Math.round(ratio * 100);
  return (
    <button
      data-testid={`project-compact-${project.id}`}
      onClick={onOpen}
      style={{
        display: 'flex', flexDirection: 'column', gap: 6,
        padding: '10px 11px', borderRadius: 10,
        background: 'var(--sf2)', border: '1px solid var(--bd)',
        cursor: 'pointer', textAlign: 'left',
        transition: 'all 0.15s',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#5BBEFC66')}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--bd)')}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <ProjectIcon name={project.icon || 'camera'} size={12} color="#5BBEFC" />
        <span style={{
          flex: 1, fontFamily: 'Sora', fontWeight: 700, fontSize: 12, color: 'var(--t1)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{project.name}</span>
        <ArrowRight size={10} color="var(--t3)" />
      </div>
      <div style={{ display: 'flex', gap: 6, fontFamily: 'Lexend', fontSize: 10, color: 'var(--t3)' }}>
        <span>{project.progress?.done || 0}/{project.progress?.total || 0}</span>
        <span>·</span>
        <span>{pct}%</span>
        {project.end_date && (<><span>·</span><span>{fmtDate(project.end_date)}</span></>)}
      </div>
      <div style={{ height: 3, borderRadius: 2, background: 'var(--bd)', overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: ratio >= 1 ? '#34D399' : '#5BBEFC',
          transition: 'width 0.4s',
        }} />
      </div>
    </button>
  );
}

function ClientDetail({ data, onOpenProject, onCreate, onDeliveryChange, hideActiveInDetail }) {
  const { client, projects, active, done, nextDeadline, avgRatio, totalTasks, doneTasks, display } = data;
  const pct = Math.round(avgRatio * 100);
  const [delivery, setDelivery] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(display || client);
  const [editingFolder, setEditingFolder] = useState(false);
  const [folderDraft, setFolderDraft] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/client-delivery/${encodeURIComponent(client)}`)
      .then((r) => {
        if (cancelled) return;
        setDelivery(r.data);
        setNameDraft(r.data.display_name || client);
        setFolderDraft(r.data.folder || '');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [client]);

  const patchStep = async (step_id, patch) => {
    // Optimistic update
    setDelivery((prev) => prev && ({
      ...prev,
      steps: prev.steps.map((s) => s.id === step_id ? { ...s, ...patch } : s),
    }));
    try {
      const r = await api.patch(`/client-delivery/${encodeURIComponent(client)}`, { step_id, ...patch });
      setDelivery(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  // Iter43b — patch display_name + folder from the header controls
  const patchMeta = async (patch) => {
    try {
      const r = await api.patch(`/client-delivery/${encodeURIComponent(client)}`, patch);
      setDelivery(r.data);
      toast.success('Enregistré');
      onDeliveryChange?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const doneCount = (delivery?.steps || []).filter((s) => s.checked).length;
  const totalCount = (delivery?.steps || []).length || 10;
  const deliveryPct = Math.round((doneCount / totalCount) * 100);

  return (
    <div data-testid={`client-detail-${client.replace(/[^A-Za-z0-9]/g, '_')}`}
      style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
    >
      {/* Header card */}
      <div className="asa-card" style={{ padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 12 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 11,
            background: 'rgba(91,190,252,0.12)',
            border: '1.5px solid #5BBEFC55',
            display: 'grid', placeItems: 'center',
          }}>
            <Briefcase size={19} color="#5BBEFC" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {editingName ? (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  data-testid="client-name-input"
                  autoFocus
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { patchMeta({ display_name: nameDraft }); setEditingName(false); }
                    if (e.key === 'Escape') { setEditingName(false); setNameDraft(display || client); }
                  }}
                  style={{
                    flex: 1, padding: '6px 10px',
                    background: 'var(--sf2)', border: '1.5px solid #5BBEFC', borderRadius: 8,
                    fontFamily: 'Sora', fontWeight: 800, fontSize: '1.25rem', color: 'var(--t1)',
                  }}
                />
                <button
                  data-testid="client-name-save"
                  onClick={() => { patchMeta({ display_name: nameDraft }); setEditingName(false); }}
                  className="asa-btn" style={{ padding: '5px 10px', fontSize: 11 }}
                >OK</button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <h1
                  data-testid="client-name-display"
                  className="asa-heading"
                  onClick={() => setEditingName(true)}
                  title="Cliquer pour renommer"
                  style={{ margin: 0, fontSize: '1.35rem', color: 'var(--t1)', cursor: 'pointer', letterSpacing: '-0.01em' }}
                >
                  {display || client}
                </h1>
                <button
                  onClick={() => setEditingName(true)}
                  style={{ padding: 3, borderRadius: 6, background: 'transparent', border: 'none', color: 'var(--t3)', cursor: 'pointer' }}
                  title="Renommer"
                ><Pencil size={12} /></button>
              </div>
            )}
            <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', marginTop: 3, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>{projects.length} projet{projects.length > 1 ? 's' : ''} · {active.length} en cours · {done.length} livré{done.length > 1 ? 's' : ''}</span>
              {nextDeadline && <span>· Deadline <strong style={{ color: 'var(--t2)' }}>{fmtDate(nextDeadline)}</strong></span>}
            </div>
            {/* Folder assignment */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
              {editingFolder ? (
                <>
                  <input
                    data-testid="client-folder-input"
                    autoFocus
                    value={folderDraft}
                    placeholder="Nom du dossier…"
                    onChange={(e) => setFolderDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { patchMeta({ folder: folderDraft }); setEditingFolder(false); }
                      if (e.key === 'Escape') { setEditingFolder(false); }
                    }}
                    style={{
                      padding: '4px 10px', background: 'var(--sf2)',
                      border: '1.5px solid #A78BFA', borderRadius: 999,
                      fontFamily: 'Sora', fontWeight: 600, fontSize: 11, color: 'var(--t1)',
                    }}
                  />
                  <button
                    data-testid="client-folder-save"
                    onClick={() => { patchMeta({ folder: folderDraft }); setEditingFolder(false); }}
                    className="asa-btn" style={{ padding: '4px 10px', fontSize: 10 }}
                  >OK</button>
                </>
              ) : (
                <button
                  data-testid="client-folder-btn"
                  onClick={() => setEditingFolder(true)}
                  style={{
                    padding: '3px 10px', borderRadius: 999,
                    background: delivery?.folder ? 'rgba(167,139,250,0.16)' : 'rgba(148,163,184,0.12)',
                    border: '1px solid ' + (delivery?.folder ? '#A78BFA55' : 'var(--bd)'),
                    color: delivery?.folder ? '#A78BFA' : 'var(--t3)',
                    fontFamily: 'Sora', fontWeight: 600, fontSize: 10.5,
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
                  }}
                >
                  <Folder size={10} /> {delivery?.folder || 'Sans dossier'}
                </button>
              )}
            </div>
          </div>
          <button
            data-testid="client-new-project-btn"
            onClick={onCreate}
            className="asa-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, height: 34 }}
          >
            <Plus size={12} /> Nouveau projet
          </button>
          {/* Consolidated more-actions menu (delete) */}
          <div style={{ position: 'relative' }}>
            <button
              data-testid="client-more-btn"
              onClick={() => setMenuOpen((v) => !v)}
              title="Plus d'actions"
              style={{
                width: 34, height: 34, borderRadius: 8,
                background: 'var(--sf2)', border: '1px solid var(--bd)',
                color: 'var(--t2)', cursor: 'pointer',
                display: 'grid', placeItems: 'center',
              }}
            ><MoreVertical size={14} /></button>
            {menuOpen && (
              <div
                onMouseLeave={() => setMenuOpen(false)}
                style={{
                  position: 'absolute', top: 38, right: 0, zIndex: 20,
                  background: 'var(--sf)', border: '1px solid var(--bd)',
                  borderRadius: 10, padding: 4, minWidth: 160,
                  boxShadow: '0 10px 24px rgba(0,0,0,0.4)',
                }}
              >
                <button
                  data-testid="client-delete-roadmap-btn"
                  onClick={async () => {
                    setMenuOpen(false);
                    if (!window.confirm(`Supprimer la roadmap de "${display || client}" ?`)) return;
                    try {
                      await api.delete(`/client-delivery/${encodeURIComponent(client)}`);
                      toast.success('Roadmap supprimée');
                      onDeliveryChange?.({ client_key: client.toLowerCase(), deleted: true });
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Erreur');
                    }
                  }}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 10px', borderRadius: 7,
                    background: 'transparent', border: 'none',
                    color: '#FF6B6B', fontFamily: 'Sora', fontWeight: 600, fontSize: 12,
                    cursor: 'pointer', textAlign: 'left',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,107,107,0.08)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <Trash2 size={12} /> Supprimer la roadmap
                </button>
              </div>
            )}
          </div>
        </div>
        {/* Overall progress */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Avancement global
            </span>
            <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, color: pct >= 100 ? '#34D399' : '#5BBEFC' }}>
              {pct}% · {doneTasks}/{totalTasks} étapes
            </span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: 'var(--sf2)', overflow: 'hidden' }}>
            <div style={{
              width: `${pct}%`, height: '100%',
              background: pct >= 100 ? '#34D399' : 'linear-gradient(90deg, #5BBEFC, #A78BFA)',
              transition: 'width 0.4s var(--ease-out)',
            }} />
          </div>
        </div>
      </div>

      {/* Process de delivery (10 étapes cochables) */}
      {delivery && (
        <div data-testid="delivery-process" className="asa-card" style={{ padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <div className="asa-eyebrow" style={{ color: '#A78BFA', fontSize: '0.58rem' }}>
                Process de delivery
              </div>
              <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 14, color: 'var(--t1)', marginTop: 3 }}>
                {doneCount}/{totalCount} étapes validées · {deliveryPct}%
              </div>
            </div>
            <div style={{
              padding: '3px 10px', borderRadius: 999,
              background: deliveryPct === 100 ? 'rgba(52,211,153,0.18)' : 'rgba(167,139,250,0.18)',
              border: '1px solid ' + (deliveryPct === 100 ? '#34D39955' : '#A78BFA55'),
              color: deliveryPct === 100 ? '#34D399' : '#A78BFA',
              fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}>
              {deliveryPct === 100 ? <><Check size={11} /> Livré</> : 'En cours'}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {delivery.steps.map((s) => (
              <DeliveryStepRow
                key={s.id}
                step={s}
                onToggle={() => patchStep(s.id, { checked: !s.checked })}
                onNotes={(notes) => patchStep(s.id, { notes })}
              />
            ))}
          </div>
        </div>
      )}

      {/* Active projects — hidden when using right panel */}
      {!hideActiveInDetail && active.length > 0 && (
        <div>
          <div className="asa-eyebrow" style={{ marginBottom: 8, color: '#5BBEFC' }}>
            <Clock size={11} style={{ verticalAlign: '-1px', marginRight: 5 }} />
            En cours ({active.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {active.map((p) => <ProjectRoadmapRow key={p.id} project={p} onOpen={() => onOpenProject(p)} />)}
          </div>
        </div>
      )}

      {/* Done projects */}
      {done.length > 0 && (
        <div>
          <div className="asa-eyebrow" style={{ marginBottom: 8, color: '#34D399' }}>
            <Check size={11} style={{ verticalAlign: '-1px', marginRight: 5 }} />
            Livrés ({done.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {done.map((p) => <ProjectRoadmapRow key={p.id} project={p} onOpen={() => onOpenProject(p)} finished />)}
          </div>
        </div>
      )}
    </div>
  );
}

function ProjectRoadmapRow({ project, onOpen, finished = false }) {
  const { total = 0, done = 0, ratio = 0 } = project.progress || {};
  const pct = Math.round(ratio * 100);
  const dl = project.end_date;
  const dlDate = dl ? new Date(dl) : null;
  const now = new Date();
  const isOverdue = dlDate && dlDate < now && ratio < 1;

  return (
    <button
      data-testid={`roadmap-project-${project.id}`}
      onClick={onOpen}
      className="asa-card"
      style={{
        padding: '12px 14px',
        display: 'grid',
        gridTemplateColumns: '30px 1fr 100px 90px 22px',
        gap: 12, alignItems: 'center',
        cursor: 'pointer', textAlign: 'left',
        opacity: finished ? 0.78 : 1,
      }}
    >
      <span style={{
        width: 26, height: 26, borderRadius: 8,
        background: `${project.color}1A`, border: `1.2px solid ${project.color}55`,
        display: 'grid', placeItems: 'center',
      }}>
        <ProjectIcon name={project.icon} size={13} color={project.color} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, color: 'var(--t1)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {project.name}
        </div>
        <div style={{ marginTop: 4, height: 4, borderRadius: 2, background: 'var(--sf2)', overflow: 'hidden' }}>
          <div style={{
            width: `${pct}%`, height: '100%',
            background: finished ? '#34D399' : project.color,
            transition: 'width 0.4s var(--ease-out)',
          }} />
        </div>
      </div>
      <span style={{
        fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
        color: finished ? '#34D399' : project.color, textAlign: 'right',
      }}>
        {pct}% · {done}/{total}
      </span>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontFamily: 'Lexend', fontSize: 11,
        color: isOverdue ? '#FF6B6B' : 'var(--t3)',
      }}>
        {isOverdue && <AlertTriangle size={10} />}
        <Calendar size={10} />
        {fmtDate(dl)}
      </span>
      <ArrowRight size={14} color="var(--t3)" />
    </button>
  );
}

// ============================================================================
// Iter43 — DeliveryStepRow : one checkable step with expandable notes
// ============================================================================
function DeliveryStepRow({ step, onToggle, onNotes }) {
  const [expanded, setExpanded] = useState(false);
  const [notesDraft, setNotesDraft] = useState(step.notes || '');
  const hasNotes = !!(step.notes || '').trim();

  return (
    <div
      data-testid={`delivery-step-${step.id}`}
      style={{
        padding: '11px 14px', borderRadius: 12,
        background: step.checked ? 'rgba(52,211,153,0.08)' : 'var(--sf2)',
        border: '1.5px solid ' + (step.checked ? 'rgba(52,211,153,0.35)' : 'var(--bd)'),
        transition: 'all 0.15s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <button
          data-testid={`delivery-step-toggle-${step.id}`}
          type="button"
          onClick={onToggle}
          style={{
            flexShrink: 0, width: 22, height: 22, borderRadius: 6,
            border: '2px solid ' + (step.checked ? '#34D399' : 'var(--bd)'),
            background: step.checked ? '#34D399' : 'transparent',
            display: 'grid', placeItems: 'center', cursor: 'pointer',
            marginTop: 2,
          }}
        >
          {step.checked && <Check size={13} color="#fff" strokeWidth={3} />}
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
            color: step.checked ? 'var(--t3)' : 'var(--t1)',
            textDecoration: step.checked ? 'line-through' : 'none',
          }}>
            Étape {step.id} · {step.title}
          </div>
          <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', marginTop: 3, lineHeight: 1.4 }}>
            {step.description}
          </div>
          {expanded ? (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <textarea
                data-testid={`delivery-step-notes-${step.id}`}
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                placeholder="Ce qu'il faut rendre / notes internes…"
                rows={2}
                style={{
                  width: '100%', padding: '8px 10px', resize: 'vertical',
                  background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 8,
                  fontFamily: 'Lexend', fontSize: 12, color: 'var(--t1)',
                }}
              />
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => { setExpanded(false); setNotesDraft(step.notes || ''); }}
                  className="asa-btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }}
                >Annuler</button>
                <button
                  data-testid={`delivery-step-save-${step.id}`}
                  type="button"
                  onClick={() => { onNotes(notesDraft); setExpanded(false); }}
                  className="asa-btn" style={{ fontSize: 11, padding: '4px 10px' }}
                >Enregistrer</button>
              </div>
            </div>
          ) : (
            <button
              data-testid={`delivery-step-expand-${step.id}`}
              type="button"
              onClick={() => setExpanded(true)}
              style={{
                marginTop: 6, fontFamily: 'Sora', fontWeight: 600, fontSize: 11,
                color: hasNotes ? '#A78BFA' : 'var(--t3)',
                cursor: 'pointer',
              }}
            >
              {hasNotes ? `📝 ${step.notes.slice(0, 60)}${step.notes.length > 60 ? '…' : ''}` : '+ Ajouter des notes'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
